import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Link } from 'react-router-dom';
import { useSelector } from "react-redux";

import { getRequest, postRequest, showMessage } from '../../../../config';

export default function Analyser({ company, getCompany, setTab, setCompanyDashboardTab, permissionLevel }) {

    let user = useSelector(state => state.user);

    let [aiReports, setAiReports] = useState([]);
    let [groupedAiReports, setGroupedAiReports] = useState([]);
    let [loadingReports, setLoadingReports] = useState(false);
    const [selectedModuleId, setSelectedModuleId] = useState('');
    const [selectedSectionId, setSelectedSectionId] = useState('');
    const [tempModules, setTempModules] = useState([]);
    const [tempSections, setTempSections] = useState([]);
    const [loadingModules, setLoadingModules] = useState(false);
    const [loadingSections, setLoadingSections] = useState(false);
    const [regeneratingReportId, setRegeneratingReportId] = useState(null);

    useEffect(() => {
        const fetchAiReports = async () => {
            if (company?._id && !loadingReports) {
                setLoadingReports(true);
                try {
                    const response = await getRequest(`/company/${company._id}/reports`);
                    if (response['code'] == 200 && response['body']?.['reports']) {
                        setAiReports(response['body']['reports']['aiReports'] || []);
                        setGroupedAiReports(response['body']['reports']['groupedAiReports'] || []);
                    }
                } catch (e) {
                    console.error('Failed to fetch AI reports:', e);
                } finally {
                    setLoadingReports(false);
                }
            }
        };
        fetchAiReports();
    }, [company?._id]);

    useEffect(() => {
        if (!company?.['_id']) return;
        const fetchTempModules = async () => {
            setLoadingModules(true);
            try {
                const response = await getRequest(`/modules/temp-modules?limit=100&isActive=true&companyId=${company['_id']}`, 'not-showMessage');
                if (response['code'] == 200 && response['body']?.['modules']) {
                    setTempModules(response['body']['modules']);
                }
            } catch (e) {
                console.error('Failed to fetch temp modules:', e);
            } finally {
                setLoadingModules(false);
            }
        };
        fetchTempModules();
    }, [company?.['_id']]);

    useEffect(() => {
        if (!selectedModuleId) { setTempSections([]); setSelectedSectionId(''); return; }
        const fetchSections = async () => {
            setLoadingSections(true);
            try {
                const rawObjective = company?.['testType'] || '';
                const companyObjective = rawObjective ? rawObjective.charAt(0).toUpperCase() + rawObjective.slice(1) : '';
                const subscriptionTier = company?.['subscription'] || '';
                const params = new URLSearchParams();
                if (companyObjective) params.set('companyObjective', companyObjective);
                if (subscriptionTier) params.set('subscriptionTier', subscriptionTier);
                if (company?.['_id']) params.set('companyId', company['_id']);
                const query = params.toString() ? `?${params.toString()}` : '';
                const response = await getRequest(`/modules/temp-sections/for-module/${selectedModuleId}${query}`, 'not-showMessage');
                if (response['code'] == 200 && response['body']?.['sections']) {
                    setTempSections(response['body']['sections']);
                }
            } catch (e) {
                console.error('Failed to fetch temp sections:', e);
            } finally {
                setLoadingSections(false);
            }
        };
        fetchSections();
    }, [selectedModuleId, company?.['testType'], company?.['subscription']]);

    const sanitizeReportLabel = (label) => {
        if (!label) return '';
        return label
            .replace(/\s+/g, '-')
            .replace(/[^a-zA-Z0-9.-]/g, '')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };

    const getLatestHistoryEntry = (history) => {
        if (!Array.isArray(history) || !history.length) return null;
        return history.reduce((latest, current) => {
            if (!latest?.lastUpdatedTime) return current;
            if (!current?.lastUpdatedTime) return latest;
            const latestDate = moment.utc(latest.lastUpdatedTime, ['MMMM Do, YYYY  hh:mma', 'MMMM Do, YYYY hh:mma']);
            const currentDate = moment.utc(current.lastUpdatedTime, ['MMMM Do, YYYY  hh:mma', 'MMMM Do, YYYY hh:mma']);
            return currentDate.isAfter(latestDate) ? current : latest;
        });
    };

    // Reason: new records store lastUpdatedTime as UTC ISO-8601 (new Date().toISOString()).
    // Legacy records stored a human-readable local-time string — fall back to parsing those as
    // UTC (they were generated on a UTC server in prod). Either way the result is shifted to the
    // viewer's local timezone for display.
    const formatHistoryTime = (timeStr) => {
        if (!timeStr) return '';
        const iso = moment(timeStr, moment.ISO_8601, true);
        if (iso.isValid()) return iso.local().format('MMM Do YYYY, h:mm A');
        const legacy = moment.utc(timeStr, ['MMMM Do, YYYY  hh:mma', 'MMMM Do, YYYY hh:mma'], true);
        if (legacy.isValid()) return legacy.local().format('MMM Do YYYY, h:mm A');
        return timeStr;
    };

    const formatLatestReportDate = (report) => {
        const latest = getLatestHistoryEntry(report?.history);
        if (latest?.lastUpdatedTime) {
            const local = formatHistoryTime(latest.lastUpdatedTime);
            const parsed = moment(local, 'MMM Do YYYY, h:mm A');
            return parsed.isValid() ? parsed.format('D MMMM, YYYY') : local;
        }
        if (report?.createdAt) return moment.utc(report.createdAt).local().format('D MMMM, YYYY');
        return '';
    };

    const selectedModule = useMemo(() => {
        if (!selectedModuleId) return null;
        return tempModules.find(m => m['_id'] === selectedModuleId) || null;
    }, [tempModules, selectedModuleId]);

    const selectedSection = useMemo(() => {
        if (!selectedSectionId) return null;
        return tempSections.find(s => s['_id'] === selectedSectionId) || null;
    }, [tempSections, selectedSectionId]);

    const filteredGroups = useMemo(() => {
        if (!groupedAiReports?.length) return [];
        const moduleKey = selectedModule?.['moduleKey'] || '';
        const sectionNo = selectedSection?.['sectionNo'] || '';
        if (sectionNo) return groupedAiReports.filter(g => g['sectionNo'] === sectionNo);
        if (moduleKey) return groupedAiReports.filter(g => g['sectionNo'].startsWith(moduleKey));
        return groupedAiReports;
    }, [groupedAiReports, selectedModule, selectedSection]);

    const handleModuleChange = (e) => {
        setSelectedModuleId(e.target.value);
        setSelectedSectionId('');
    };

    const handleSectionChange = (e) => {
        setSelectedSectionId(e.target.value);
    };

    const getReportSectionId = (report) => {
        const rt = report?.['reportType'] || '';
        if (!rt.startsWith('sectionReport_')) return null;
        return rt.replace('sectionReport_', '');
    };

    const handleRegenerate = async (report) => {
        const sectionId = getReportSectionId(report);
        if (!sectionId) return showMessage('Select a section to regenerate this report', 'error');
        const files = report['fileurl'];
        if (!files || !files.length) return showMessage('No source files found on this report', 'error');
        setRegeneratingReportId(report['_id']);
        try {
            const res = await postRequest(`/modules/generateTempSectionReport/${company['_id']}/${sectionId}`, { files, isQuestion: false, focusArea: report['focusArea'] || null });
            if (res['code'] == 200) {
                showMessage('Report regenerated successfully', 'success');
                const updated = await getRequest(`/company/${company['_id']}/reports`);
                if (updated['code'] == 200 && updated['body']?.['reports']) {
                    setAiReports(updated['body']['reports']['aiReports'] || []);
                    setGroupedAiReports(updated['body']['reports']['groupedAiReports'] || []);
                }
            } else {
                showMessage(res['message'] || 'Failed to regenerate report', 'error');
            }
        } catch (e) {
            console.error('Regenerate report error:', e);
            showMessage('Failed to regenerate report', 'error');
        } finally {
            setRegeneratingReportId(null);
        }
    };

    const handleReportRedirect = (report) => {
        if ((user['role'] == "Advisor" || user['role'] == "Investor") && (permissionLevel == 'DASHBOARD')) {
            return;
        };

        const tabIdentifier = report['reportLabel']
            ? sanitizeReportLabel(report['reportLabel'])
            : report['_id'];
        setTab(`${tabIdentifier}-analyser`);
        setCompanyDashboardTab('Dashboard');
    };

    // Reason: history versions open in the same analyser tab as the latest report, but the
    // history entry id travels inside the tab value (before the -analyser suffix so routing that
    // relies on tab.endsWith('-analyser') keeps working). InsuranceGapAnalysisReport reads this
    // ?history=<id> and renders that snapshot instead of the latest htmlReport.
    const handleHistoryRedirect = (report, entry) => {
        if ((user['role'] == "Advisor" || user['role'] == "Investor") && (permissionLevel == 'DASHBOARD')) return;
        if (!entry?.['_id']) return;
        const tabIdentifier = report['reportLabel']
            ? sanitizeReportLabel(report['reportLabel'])
            : report['_id'];
        setTab(`${tabIdentifier}?history=${entry['_id']}-analyser`);
        setCompanyDashboardTab('Dashboard');
    };

    const reportCard = (report) => {
        let creator;
        if (report?.['createdBy']) creator = report?.['createdBy'];
        const reportTitle = report['reportLabel'] || report['name'] || 'Name of the analysis report';
        const canOpen = !!report?.['htmlReport'] && permissionLevel != 'DASHBOARD';

        return (
            <div
                key={report['_id']}
                className="bo29 bss bw1px bg1 br15px p1-5rem mx0-75rem my1rem pr bsbb df fdc jcsb"
                style={{ width: '210px', minWidth: '210px', minHeight: '280px', flex: '0 0 180px' }}
            >
                <div className="w100">
                    <div className="df aic jcsb mb1-5rem">
                        <p className="fs0-75rem fw400 c70">Reporter</p>
                        <div className="df aic">
                            {/* {canOpen && (
                                <i
                                    data-tooltip="Edit"
                                    onClick={() => handleReportRedirect(report)}
                                    className="cp fa fa-edit c3 mr0-5rem"
                                    style={{ fontSize: '16px' }}
                                />
                            )} */}

                            {regeneratingReportId === report['_id']
                                ? <i className="fa fa-spinner fa-spin c3 mr0-5rem" style={{ fontSize: '16px' }} />
                                : <i
                                    data-tooltip="Regenerate Report"
                                    onClick={() => handleRegenerate(report)}
                                    className="cp fa fa-refresh c3 mr0-5rem"
                                    style={{ fontSize: '16px' }}
                                />
                            }

                            {(report?.['history'] && report['history'].length > 0) && (
                                <div className="cp module-tooltip-box">
                                    <div className="hover-list" style={{ position: 'relative', left: 'auto', top: 'auto' }}>
                                        <i data-tooltip="History" className="cp fa fa-history c4" style={{ fontSize: '16px', marginTop: '4px' }} />
                                    </div>
                                    <span className="tooltip-area tooltip-reaserchaid">
                                        {report['history'].map((data, idx) => {
                                            const hasHtmlReport = Array.isArray(data?.['htmlReport']) && data['htmlReport'].length > 0;
                                            return (
                                                <div
                                                    key={data['_id'] || idx}
                                                    className={`overlay-box ${hasHtmlReport ? 'cp' : 'cna op50'}`}
                                                    style={{ padding: '5px 10px' }}
                                                    data-tooltip={hasHtmlReport ? 'View this generated report' : 'No saved report available for this version'}
                                                    onClick={() => hasHtmlReport && handleHistoryRedirect(report, data)}
                                                >
                                                    <div className="df aic w100">
                                                        {data?.['userId'] &&
                                                            <span data-tooltip={`${data['userId']?.firstName ?? ''} ${data['userId']?.lastName ?? ''}`} className="shadow w2rem h2rem bg16 bw3px bss bo19 df jcc aic br100 c1 mr0-5rem">
                                                                {data['userId']['profile']
                                                                    ? <img src={data['userId']['profile']} className="new-shadow w30px h30px df jcc aic br100 bg16 c1" alt="" />
                                                                    : <span className="bg16 c15 br50px">
                                                                        {data['userId']?.firstName ? (data['userId'].firstName[0] + (data['userId'].lastName?.[0] || '')) : '?'}
                                                                    </span>
                                                                }
                                                            </span>
                                                        }
                                                        <div className="text-box df aic jcsb w100">
                                                            <p className="c15" style={{ fontSize: '10px' }}>{formatHistoryTime(data['lastUpdatedTime'])}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {canOpen
                        ? <Link
                            to={`#${sanitizeReportLabel(report['reportLabel'] || report['_id'])}-analyser`}
                            onClick={() => handleReportRedirect(report)}
                            className="db w100 fs1rem fw600 c15 lh1-5 ttc"
                        >
                            {reportTitle} Report
                        </Link>
                        : <p className="w100 fs1rem fw600 c15 lh1-5 ttc">{reportTitle}</p>
                    }
                </div>

                <div className="w100 df aic mt2rem">
                    {creator
                        ? <span data-tooltip={`${creator?.firstName ?? ''} ${creator?.lastName ?? ''}`} className="shadow w30px h30px bg16 bw2px bss bo19 df jcc aic br100 c1 mr0-5rem">
                            {creator?.profile
                                ? <img src={creator.profile} className="br100" style={{ width: '30px', height: '30px' }} alt="" />
                                : <span className="bg16 c15 fs0-75rem">
                                    {creator?.firstName ? (creator.firstName[0] + (creator.lastName?.[0] || '')) : '?'}
                                </span>
                            }
                        </span>
                        : <span className="w30px h30px bg16 br100 mr0-5rem" />
                    }
                    <p className="fs0-75rem fw400 c15">{formatLatestReportDate(report) || '—'}</p>
                </div>
            </div>
        );
    };

    const dummyReportCard = (title = 'Name of the report here') => {
        return (
            <div
                className="bo29 bss bw1px bg1 br15px p1-5rem mx0-75rem my1rem pr bsbb df fdc jcsb op60"
                style={{ width: '210px', minWidth: '210px', minHeight: '280px', flex: '0 0 180px' }}
            >
                <div className="w100">
                    <div className="df aic jcsb mb1-5rem">
                        <p className="fs0-75rem fw400 c28">Reporter</p>
                        <div className="df aic">
                            <i data-tooltip="Edit" className="fa fa-edit c3 mr0-5rem" style={{ fontSize: '16px' }} />
                            <i data-tooltip="History" className="fa fa-history c4" style={{ fontSize: '16px' }} />
                        </div>
                    </div>
                    <p className="w100 fs1rem fw600 c15 lh1-5">{title}</p>
                </div>
                <div className="w100 df aic mt2rem">
                    <span className="w30px h30px bg16 br100 mr0-5rem" />
                    <p className="fs0-75rem fw400 c28">8 March, 2025</p>
                </div>
            </div>
        );
    };

    const upgradeCard = (subscription) => {
        const navigationUrl = ['Advisor', 'Investor'].includes(company?.['servicedBy']?.['role'] || user['role']) ? `/billing/${company['_id']}/pro/${user['role'].toLowerCase() === "user" ? company?.['servicedBy']?.['role'].toLowerCase() : user['role'].toLowerCase()}` : `/billing/${company['_id']}`;
        return <div className='shadow bg1 tac w100 p1rem br30px' style={{ height: '212px' }}>
            <h4 className="pr" style={{ bottom: '20%' }}><span className="shadow c59 fs1-25rem px1-5rem br10px bw1px bo59 bss ttu" style={{ background: '#E4FFEB' }}>{subscription}</span></h4>
            <div className="subs-btn mt0-5rem">
                <p className="fs1rem fw500 c15 mb0-5rem">Have you thought <br /> about upgrading?</p>
                <Link to={navigationUrl} className="bg4 bg3-hover fs0-75rem fw600 c1 td250 br30px px3rem py0-5rem cp tac mt0-5rem">Find out more</Link>
            </div>
        </div>
    };

    return (
        <>
            <div className='sectionWrapper py1rem fs0-75rem pr anylist-builder-tab'>
                <div className="df aic jcsb w100 mb1-5rem fww" style={{ gap: '1rem' }}>
                    <div></div>
                    {/* <div
                        data-tooltip="Return to the relevant module to generate analysis reports"
                        className="df bg1 br50px bw2px bss bo29 pr1rem p0-5rem bsbb aic"
                        style={{ width: '206px' }}
                    >
                        <span className="bg1 bsbb mr0-5rem br100 w40px h40px tac df jcc aic">
                            <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clipPath="url(#clip0_analyser_builder)">
                                    <path d="M19 0C8.52298 0 0 8.52298 0 19C0 29.477 8.52298 38 19 38C29.477 38 38 29.477 38 19C38 8.52298 29.477 0 19 0Z" fill="#4A67FF" />
                                    <path d="M27.3098 20.5805H20.5805V27.3098C20.5805 28.1839 19.8714 28.893 18.9973 28.893C18.1232 28.893 17.4141 28.1839 17.4141 27.3098V20.5805H10.6848C9.8107 20.5805 9.10156 19.8714 9.10156 18.9973C9.10156 18.1232 9.8107 17.4141 10.6848 17.4141H17.4141V10.6848C17.4141 9.8107 18.1232 9.10156 18.9973 9.10156C19.8714 9.10156 20.5805 9.8107 20.5805 10.6848V17.4141H27.3098C28.1839 17.4141 28.893 18.1232 28.893 18.9973C28.893 19.8714 28.1839 20.5805 27.3098 20.5805Z" fill="#FAFAFA" />
                                </g>
                                <defs>
                                    <clipPath id="clip0_analyser_builder">
                                        <rect width="38" height="38" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg>
                        </span>
                        <span className="c15 fs0-875rem fw400">Analyser Builder</span>
                    </div> */}

                    <div className="df aic fww" style={{ gap: '0.75rem' }}>
                        <div className="pr" style={{ width: '180px' }}>
                            <select
                                value={selectedModuleId}
                                onChange={handleModuleChange}
                                disabled={loadingModules}
                                className={`bg1 br50px bw1px bss bo29 px1-5rem py0-75rem fs0-875rem c28 fsi w100 bsbb ${loadingModules ? 'cna op60' : 'cp'}`}
                                style={{ appearance: 'none', WebkitAppearance: 'none', paddingRight: '2.5rem' }}
                            >
                                <option value="">{loadingModules ? 'Loading...' : 'All Modules'}</option>
                                {!loadingModules && tempModules.map(mod => (
                                    <option key={mod['_id']} value={mod['_id']}>{mod['moduleName']}</option>
                                ))}
                            </select>
                            {loadingModules
                                ? <i className="fa fa-spinner fa-spin pa c3" style={{ right: '1rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '10px' }} />
                                : <i className="fa fa-chevron-down pa c28" style={{ right: '1rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '10px' }} />
                            }
                        </div>
                        <div className="pr" style={{ width: '180px' }}>
                            <select
                                value={selectedSectionId}
                                onChange={handleSectionChange}
                                disabled={!selectedModuleId || loadingSections}
                                className={`bg1 br50px bw1px bss bo29 px1-5rem py0-75rem fs0-875rem c28 fsi w100 bsbb ${selectedModuleId && !loadingSections ? 'cp' : 'cna op60'}`}
                                style={{ appearance: 'none', WebkitAppearance: 'none', paddingRight: '2.5rem' }}
                            >
                                <option value="">{loadingSections ? 'Loading...' : 'All Sections'}</option>
                                {!loadingSections && tempSections.map((section, idx) => (
                                    <option key={section['_id'] || idx} value={section['_id'] || ''}>
                                        {section['sectionName']}
                                    </option>
                                ))}
                            </select>
                            {loadingSections
                                ? <i className="fa fa-spinner fa-spin pa c3" style={{ right: '1rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '10px' }} />
                                : <i className="fa fa-chevron-down pa c28" style={{ right: '1rem', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '10px' }} />
                            }
                        </div>
                    </div>
                </div>

                {loadingReports
                    ? <div className='mt0-5rem bss px1-5rem py2rem br25px w100 keyfeature-box-portfolio anylist-checklist df fww mb3rem pr bsbb' style={{ background: '#F5F6F9' }}>
                        <div className="w100 tac py3rem"><p className="fs1rem c12">Loading reports...</p></div>
                    </div>
                    : filteredGroups.length > 0
                        ? filteredGroups.map(group => (
                            <div key={group['sectionNo']} className='mt0-5rem bss px1-5rem py2rem br25px w100 keyfeature-box-portfolio anylist-checklist df fww mb3rem pr bsbb' style={{ background: '#F5F6F9' }}>
                                <div className="df aic w100 pl0-5rem mb1-5rem">
                                    {/* <span className="fs0-75rem fw600 c1 bg3 br50px px0-75rem py0-25rem mr0-75rem wsn">{group['sectionNo']}</span> */}
                                    <h3 className="fs1-25rem fw600 c15">{group['sectionName']}</h3>
                                </div>
                                <div className="w100 df fww aifs" style={{ gap: '0.25rem' }}>
                                    {group['reports'].map(item => reportCard(item))}
                                </div>
                                <div className="research-aid-ai-img" style={{ height: '280px' }}>
                                    <img className="pa r0 b0" src={require(`../../../../../images/research-aid-ai.png`)} alt="ai-analyser" />
                                </div>
                            </div>
                        ))
                        : <div className='mt0-5rem bss px1-5rem py2rem br25px w100 keyfeature-box-portfolio anylist-checklist df fww mb3rem pr bsbb' style={{ background: '#F5F6F9' }}>
                            <div className="w100 tac py3rem">
                                <p className="fs1rem c12">
                                    {aiReports.length > 0 ? 'No reports match the selected module' : 'No AI reports available yet'}
                                </p>
                            </div>
                            <div className="research-aid-ai-img" style={{ height: '280px' }}>
                                <img style={{ position: 'absolute', bottom: '0', right: '0' }} src={require(`../../../../../images/analyser.png`)} alt="ai-analyser" />
                            </div>
                        </div>
                }

                {/* {company['subscription'] == 'Free' &&
                    <div className='mt1rem bss px1rem py2rem br25px w100 keyfeature-box-portfolio anylist-checklist df fww mb3rem pr bsbb'>
                        <h3 className="fs1-25rem c15 w100 pl1rem">AI Analyser Reports - <span className='fw600 c7 ttu'>{['Advisor', 'Investor'].includes(company?.['servicedBy']?.['role'] || user['role']) ? 'Pro' : 'Lite'}</span></h3>
                        <div className='w100 keyfeature-box keyfeature-box-company-pro df fww mb2rem pr bsbb mt2rem'>
                            <div className="grs1-md df fdc aic tac w20">
                                {upgradeCard(['Advisor', 'Investor'].includes(company?.['servicedBy']?.['role'] || user['role']) ? 'Pro' : 'Lite')}
                            </div>
                            <div className="df fww w80 coreworkspace-update">
                                {dummyReportCard('Your AI Report Here')}
                            </div>
                        </div>
                        <div className="research-aid-ai-img">
                            {aiReports && <img className="pa r0 b0" src={require(`../../../../../images/research-aid-ai-lite.png`)} alt="ai-analyser" />}
                        </div>
                    </div>
                } */}

                {/* {(company['subscription'] == 'Free' || company['subscription'] == 'Lite') &&
                    <div className='mt1rem bss px1rem py2rem br25px w100 keyfeature-box-portfolio anylist-checklist df fww mb3rem pr bsbb'>
                        <h3 className="fs1-25rem c15 w100 pl1rem">AI Analyser Reports - <span className='fw600 c7 ttu'>PRO</span></h3>
                        <div className='w100 keyfeature-box keyfeature-box-company-pro df fww mb2rem pr bsbb mt2rem'>
                            <div className="grs1-md df fdc aic tac w20">
                                {upgradeCard('Pro')}
                            </div>
                            <div className="df fww w80 coreworkspace-update">
                                {dummyReportCard('Name of the report here')}
                            </div>
                        </div>
                    </div>
                } */}
            </div>
        </>
    )
}
